import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  TextField,
  Grid,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from "@mui/material";
import { Icon } from "@iconify/react";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch, useSelector } from "react-redux";
import { usePagination } from "app/hooks/usePagination";
import { Table as CustomTable } from "app/components";
import { getMeeshoOrdersList } from "app/redux/slice/meeshoSlice";
import Loader from "@jumbo/components/Loader";
import OrderDetailDialog from "./order-detail";
import { useNavigate } from "react-router-dom";
import { MEESHO } from "app/routes/constant";
import VisibilityIcon from "@mui/icons-material/Visibility";

const MeeshoOrdersList = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const meeshoData = useSelector((state) => state?.meesho);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [finalStatusFilter, setFinalStatusFilter] = useState("all");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "meesho-orders" });

  // Get orders data from Redux store
  const orders = meeshoData?.orders?.list || [];

  // Load orders data
  const getOrdersData = () => {
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchTerm.trim(),
      status: statusFilter !== "all" ? statusFilter : undefined,
      finalStatus: finalStatusFilter !== "all" ? finalStatusFilter : undefined,
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getMeeshoOrdersList(value));
  };

  useEffect(() => {
    getOrdersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch, order, orderBy, statusFilter]);

  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "DELIVERED":
        return "info";
      case "RTO_COMPLETE":
        return "warning";
      case "CANCELLED":
        return "error";
      case 1:
        return "info";
      case 2:
        return "success";
      default:
        return "default";
    }
  };

  // Define columns for CustomTable based on actual data structure
  const columns = [
    {
      id: "subOrderNumber",
      label: "Sub Order No",
      sort: true,
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: "bold", color: "#2874f0" }}
        >
          {row.subOrderNumber}
        </Typography>
      ),
    },
    {
      id: "productName",
      label: "Product",
      sort: true,
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {row.productName}
        </Typography>
      ),
    },
    {
      id: "sku",
      label: "SKU",
      sort: true,
      render: (row) => (
        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
          {row.sku}
        </Typography>
      ),
    },
    {
      id: "quantity",
      label: "Qty",
      sort: true,
      align: "center",
    },
    {
      id: "listedPrice",
      label: "Amount",
      sort: true,
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          ₹{row.listedPrice?.toLocaleString() || 0}
        </Typography>
      ),
    },
    {
      id: "orderState",
      label: "Status",
      sort: true,
      render: (row) => (
        <Chip
          label={row?.orderState?.toUpperCase()?.replace(/_/g, " ")}
          color={getStatusColor(row.orderState)}
          size="small"
        />
      ),
    },
    {
      id: "orderFinalStatus",
      label: "Final Status",
      sort: false,
      render: (row) => (
        <Chip
          label={row?.orderFinalStatus === 1 ? "In Process" : "Completed"}
          color={getStatusColor(row?.orderFinalStatus)}
          size="small"
        />
      ),
    },
    {
      id: "orderDate",
      label: "Order Date",
      sort: true,
      render: (row) => new Date(row.orderDate).toLocaleDateString(),
    },
    {
      id: "customerState",
      label: "Customer State",
      sort: true,
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <IconButton
          onClick={(e) => {
            setSelectedOrderId(row._id);
            setOpenDetailDialog(true);
          }}
          size="small"
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getOrdersData();
    }
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedOrderId(null);
  };

  return (
    <Box>
      {meeshoData?.orders?.loading && <Loader />}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <Typography variant="h3" sx={{ fontWeight: "bold" }}>
          Invoice Management
        </Typography>
        <Button
          variant="contained"
          size="small"
          sx={{ width: "100px" }}
          startIcon={<Icon icon="ep:back" width="24" height="24" />}
          onClick={() => navigate(MEESHO)}
        >
          Back
        </Button>
      </div>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search "
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <Icon
                      icon="material-symbols:search"
                      style={{ marginRight: "8px" }}
                    />
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      style={{
                        display: searchTerm === "" ? "none" : "flex",
                      }}
                      onClick={() => setSearchTerm("")}
                    >
                      <ClearIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Final Status</InputLabel>
                <Select
                  value={finalStatusFilter}
                  label="Final Status"
                  onChange={(e) => setFinalStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="1">In Process</MenuItem>
                  <MenuItem value="2">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Orders</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  <MenuItem value="DELIVERED">Delivered</MenuItem>
                  <MenuItem value="RTO_COMPLETE">RTO Complete</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  handleChangePage(0, 0);
                  getOrdersData();
                }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent>
          <CustomTable
            columns={columns}
            rows={orders}
            page={page}
            rowsPerPage={rowsPerPage}
            count={meeshoData?.orders?.total || 0}
            Order={order}
            OrderBy={orderBy}
            sortData={sortData}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            loading={meeshoData?.orders?.loading}
          />
        </CardContent>
      </Card>

      {/* Order Detail Dialog */}
      <OrderDetailDialog
        open={openDetailDialog}
        onClose={handleCloseDetailDialog}
        orderId={selectedOrderId}
      />
    </Box>
  );
};

export default MeeshoOrdersList;
